import { ResultV2 } from '@xemplo/gp-types';

import {
  BusinessUnitIntegrationSetting,
  IntegrationSettings,
  IntegrationSettingsData,
  IntegrationSettingsModel,
  SettingName,
} from './business-unit-query.types';

/**
 * Case-insensitively resolves an API `settingName` string to a known `SettingName`.
 * Returns `undefined` for unknown/empty names so an unexpected value cannot silently
 * masquerade as a real setting. See docs/adr/0003.
 */
export function parseSettingName(value?: string | null): SettingName | undefined {
  if (!value) return undefined;
  return Object.values(SettingName).find(
    (name) => name.toLowerCase() === value.toLowerCase(),
  );
}

function toDerivedSettings(rows: BusinessUnitIntegrationSetting[]): IntegrationSettings {
  const settings: IntegrationSettings = {};
  for (const row of rows) {
    const name = parseSettingName(row.settingName);
    if (name) settings[name] = row.isIntegrated;
  }
  return settings;
}

/**
 * Maps the GET envelope into the query shape: the raw rows (the write source of truth
 * for a full-replace PUT) plus a derived boolean-per-setting Record for display and
 * form defaults. An empty/`204` response yields empty data.
 */
export function mapIntegrationSettings(
  data?: ResultV2<BusinessUnitIntegrationSetting[]>,
): IntegrationSettingsData {
  const rows = data?.result ?? [];
  return { rows, settings: toDerivedSettings(rows) };
}

/**
 * Builds the full-replace PUT body from the form values and the rows the GET returned.
 * Under full-replace, omitting a row deletes it — so preservation is the default:
 * start from the GET rows (echoing `id` + `keypayReportUserId`), overwrite
 * `isIntegrated` for rendered settings from the form, pass unrendered/unknown rows
 * through untouched, and append rendered settings the GET lacked as new rows
 * (`id: null`). See docs/adr/0003.
 */
export function buildIntegrationSettingsPayload(
  values: IntegrationSettings,
  originalRows: BusinessUnitIntegrationSetting[],
): IntegrationSettingsModel[] {
  const covered = new Set<SettingName>();

  const merged = originalRows.map((row) => {
    const name = parseSettingName(row.settingName);
    const isRendered = name !== undefined && name in values;
    if (name) covered.add(name);
    return {
      id: row.id,
      settingName: row.settingName ?? '',
      isIntegrated: isRendered ? !!values[name] : row.isIntegrated,
      keypayReportUserId: row.keypayReportUserId ?? null,
    };
  });

  const appended = (Object.keys(values) as SettingName[])
    .filter((name) => !covered.has(name))
    .map((name) => ({
      id: null,
      settingName: name,
      isIntegrated: !!values[name],
    }));

  return [...merged, ...appended];
}
